import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import Layout from "@/components/app/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Pemasukan from "@/pages/Pemasukan";
import KategoriPage from "@/pages/KategoriPage";
import MasterPembeli from "@/pages/MasterPembeli";
import Pengeluaran from "@/pages/Pengeluaran";
import LaporanSemua from "@/pages/LaporanSemua";
import LaporanPPh05 from "@/pages/LaporanPPh05";
import LaporanPPhNormal from "@/pages/LaporanPPhNormal";
import UsersPage from "@/pages/UsersPage";
import AuditLog from "@/pages/AuditLog";
import Trash from "@/pages/Trash";
import Settings from "@/pages/Settings";
import LabaRugi from "@/pages/LabaRugi";
import Neraca from "@/pages/Neraca";
import ArusKas from "@/pages/ArusKas";
import Profile from "@/pages/Profile";
import SuperAdminUsers from "@/pages/SuperAdminUsers";
import SuperAdminAudit from "@/pages/SuperAdminAudit";
import SuperAdminSecurity from "@/pages/SuperAdminSecurity";
import SuperAdminBusinessTypes from "@/pages/SuperAdminBusinessTypes";
import Produksi from "@/pages/Produksi";
import Dividen from "@/pages/Dividen";
import HutangPiutang from "@/pages/HutangPiutang";
import PiutangReminder from "@/pages/PiutangReminder";
import PemasukanPribadi from "@/pages/PemasukanPribadi";
import PengeluaranPribadi from "@/pages/PengeluaranPribadi";
import PajakPribadi from "@/pages/PajakPribadi";
import HartaPage from "@/pages/HartaPage";
import UtangPage from "@/pages/UtangPage";
import TanggunganPage from "@/pages/TanggunganPage";
import PengaturanPajakPribadi from "@/pages/PengaturanPajakPribadi";
import ImporMutasi from "@/pages/ImporMutasi";
import AkunPage from "@/pages/AkunPage";
import TransferPage from "@/pages/TransferPage";
import KreditPajakPage from "@/pages/KreditPajakPage";
import DashboardPribadi from "@/pages/DashboardPribadi";
import WorkspaceOnly from "@/components/app/WorkspaceOnly";
import SuperAdminOnly from "@/components/app/SuperAdminOnly";
import EnterToSave from "@/components/app/EnterToSave";
import RiwayatBayarPajak from "@/pages/RiwayatBayarPajak";
import BackupPage from "@/pages/BackupPage";

function HomeRedirect() {
  const { user } = useAuth();
  if (user?.role === "super_admin") return <Navigate to="/superadmin/users" replace/>;
  return <Navigate to="/dashboard" replace/>;
}

function DashboardSwitch() {
  const { user } = useAuth();
  return user?.workspace_type === "pribadi" ? <DashboardPribadi/> : <Dashboard/>;
}

function PemasukanSwitch() {
  const { user } = useAuth();
  return user?.workspace_type === "pribadi" ? <PemasukanPribadi/> : <Pemasukan/>;
}

function PengeluaranSwitch() {
  const { user } = useAuth();
  return user?.workspace_type === "pribadi" ? <PengeluaranPribadi/> : <Pengeluaran/>;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors/>
          <EnterToSave/>
          <Routes>
            <Route path="/login" element={<Login/>}/>
            <Route path="/forgot-password" element={<ForgotPassword/>}/>
            <Route path="/reset-password" element={<ResetPassword/>}/>
            <Route element={<ProtectedRoute><Layout/></ProtectedRoute>}>
              <Route path="/" element={<HomeRedirect/>}/>
              <Route path="/dashboard" element={<WorkspaceOnly><DashboardSwitch/></WorkspaceOnly>}/>
              <Route path="/produksi" element={<WorkspaceOnly><Produksi/></WorkspaceOnly>}/>
              <Route path="/produksi/kategori" element={<WorkspaceOnly><KategoriPage tipe="produk" title="Input Kategori Produk" subtitle="Kelola produk arang (mis. Arang Kayu Tungku, Batok Kelapa, Briket). Produksi, penjualan, stok & HPP dihitung terpisah per produk." testidPrefix="kat-produk"/></WorkspaceOnly>}/>
              <Route path="/pajak/riwayat-bayar" element={<WorkspaceOnly><RiwayatBayarPajak/></WorkspaceOnly>}/>
              <Route path="/piutang" element={<WorkspaceOnly><PiutangReminder/></WorkspaceOnly>}/>
              <Route path="/pemasukan" element={<WorkspaceOnly><PemasukanSwitch/></WorkspaceOnly>}/>
              <Route path="/pemasukan/kategori" element={<WorkspaceOnly><KategoriPage tipe="pemasukan" title="Input Kategori Pemasukan" subtitle="Kelola kategori untuk transaksi pemasukan." testidPrefix="kat-in"/></WorkspaceOnly>}/>
              <Route path="/pemasukan/pembeli" element={<WorkspaceOnly><MasterPembeli/></WorkspaceOnly>}/>
              <Route path="/pengeluaran" element={<WorkspaceOnly><PengeluaranSwitch/></WorkspaceOnly>}/>
              <Route path="/pengeluaran/kategori" element={<WorkspaceOnly><KategoriPage tipe="pengeluaran" title="Input Kategori Pengeluaran" subtitle="Kelola kategori untuk transaksi pengeluaran." testidPrefix="kat-out"/></WorkspaceOnly>}/>
              <Route path="/laporan/semua" element={<WorkspaceOnly><LaporanSemua/></WorkspaceOnly>}/>
              <Route path="/laporan/pph-05" element={<WorkspaceOnly><LaporanPPh05/></WorkspaceOnly>}/>
              <Route path="/laporan/pph-normal" element={<WorkspaceOnly><LaporanPPhNormal/></WorkspaceOnly>}/>
              <Route path="/laporan/laba-rugi" element={<WorkspaceOnly><LabaRugi/></WorkspaceOnly>}/>
              <Route path="/laporan/neraca" element={<WorkspaceOnly><Neraca/></WorkspaceOnly>}/>
              <Route path="/laporan/arus-kas" element={<WorkspaceOnly><ArusKas/></WorkspaceOnly>}/>
              <Route path="/laporan/dividen" element={<WorkspaceOnly><Dividen/></WorkspaceOnly>}/>
              <Route path="/hutang-piutang" element={<WorkspaceOnly><HutangPiutang/></WorkspaceOnly>}/>
              <Route path="/hutang-piutang/kategori" element={<WorkspaceOnly><KategoriPage tipe="hutang_piutang" title="Input Kategori Utang & Piutang" subtitle="Kelola kategori untuk transaksi utang & piutang." testidPrefix="kat-hp"/></WorkspaceOnly>}/>
              <Route path="/pribadi/pajak" element={<WorkspaceOnly><PajakPribadi/></WorkspaceOnly>}/>
              <Route path="/pribadi/harta" element={<WorkspaceOnly><HartaPage/></WorkspaceOnly>}/>
              <Route path="/pribadi/utang" element={<WorkspaceOnly><UtangPage/></WorkspaceOnly>}/>
              <Route path="/pribadi/tanggungan" element={<WorkspaceOnly><TanggunganPage/></WorkspaceOnly>}/>
              <Route path="/pribadi/pengaturan-pajak" element={<WorkspaceOnly><PengaturanPajakPribadi/></WorkspaceOnly>}/>
              <Route path="/pribadi/akun" element={<WorkspaceOnly><AkunPage/></WorkspaceOnly>}/>
              <Route path="/pribadi/transfer" element={<WorkspaceOnly><TransferPage/></WorkspaceOnly>}/>
              <Route path="/pribadi/impor-mutasi" element={<WorkspaceOnly><ImporMutasi/></WorkspaceOnly>}/>
              <Route path="/pribadi/kredit-pajak" element={<WorkspaceOnly><KreditPajakPage/></WorkspaceOnly>}/>
              <Route path="/pengaturan" element={<WorkspaceOnly><Settings/></WorkspaceOnly>}/>
              <Route path="/pengaturan/users" element={<WorkspaceOnly><UsersPage/></WorkspaceOnly>}/>
              <Route path="/pengaturan/audit" element={<WorkspaceOnly><AuditLog/></WorkspaceOnly>}/>
              <Route path="/pengaturan/backup" element={<WorkspaceOnly><BackupPage/></WorkspaceOnly>}/>
              <Route path="/pengaturan/sampah" element={<WorkspaceOnly><Trash/></WorkspaceOnly>}/>
              <Route path="/pengaturan/profil" element={<Profile/>}/>
              <Route path="/superadmin/users" element={<SuperAdminOnly><SuperAdminUsers/></SuperAdminOnly>}/>
              <Route path="/superadmin/audit" element={<SuperAdminOnly><SuperAdminAudit/></SuperAdminOnly>}/>
              <Route path="/superadmin/security" element={<SuperAdminOnly><SuperAdminSecurity/></SuperAdminOnly>}/>
              <Route path="/superadmin/business-types" element={<SuperAdminOnly><SuperAdminBusinessTypes/></SuperAdminOnly>}/>
            </Route>
            <Route path="*" element={<Navigate to="/" replace/>}/>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
